import React, { useMemo, useRef, useState } from 'react';
import { PreLoad } from '@teko/ecommerce';
import { JustForYou, ActionLink } from '@teko/teko-ecommerce';
import {
  useNewProductCard,
  useDiscoverySearch,
  useTenantConfig,
} from '../hooks';
import { LIMIT_ITEM_PER_PAGE_PRODUCT_LIST } from 'constants/index';
import { DiscoveryProduct } from 'types';

const HEADER_HEIGHT = 56;

interface DiscoveryRecommendedProductsProps {
  paramsObject?: any;
  title?: string;
  actionLink?: ActionLink;
  // need pass props with specific requirement: Ex: MML need 10 items / page
  itemsPerPage?: number;
  serverProducts?: DiscoveryProduct[];
}

export const DiscoveryRecommendedProducts: React.FunctionComponent<
  DiscoveryRecommendedProductsProps
> = ({
  paramsObject,
  title,
  actionLink,
  itemsPerPage,
  serverProducts = [],
}: DiscoveryRecommendedProductsProps) => {
  const { convertNewProductCard } = useNewProductCard();
  const { terminal, location } = useTenantConfig();
  const [pageNumber, setPageNumber] = useState(1);
  const PAGE_SIZE = itemsPerPage || LIMIT_ITEM_PER_PAGE_PRODUCT_LIST;
  const { result, isLoading } = useDiscoverySearch({
    ...paramsObject,
    terminalCode: terminal,
    pageNumber: pageNumber,
    itemsPerPage: PAGE_SIZE,
    location,
  });

  const top = useRef(null);

  const newProducts = useMemo(() => {
    return (result?.products?.length ? result.products : serverProducts).map(
      (product) => convertNewProductCard(product),
    );
  }, [result]);

  const handlePageChanged = (page: number) => {
    if (top && top.current) {
      const element = top.current;
      window.scrollTo({
        left: 0,
        top: getElementOffset(element).top - HEADER_HEIGHT - 30,
      });
    }
    setPageNumber(page);
  };

  if (!newProducts.length) {
    return isLoading ? <PreLoad height={1000} isLoading={isLoading} /> : null;
  }

  const totalItems = result.pagination.totalItems;

  return (
    <div ref={top}>
      <JustForYou
        header={{
          title: title,
          link: (totalItems > PAGE_SIZE && actionLink) || undefined,
        }}
        products={newProducts}
        productsPerRow={5}
        pagination={{
          totalRecords: totalItems,
          currentPage: pageNumber,
          pageLimit: PAGE_SIZE,
          pageNeighbor: 2,
          onPageChange: handlePageChanged,
          color: 'primary500',
        }}
      />
    </div>
  );
};

//get element offset relative to the document
const getElementOffset = (elem: HTMLElement) => {
  const box = elem.getBoundingClientRect();
  const body = document.body;
  const docEl = document.documentElement;
  const scrollTop = window.pageYOffset || docEl.scrollTop || body.scrollTop;
  const scrollLeft = window.pageXOffset || docEl.scrollLeft || body.scrollLeft;
  const clientTop = docEl.clientTop || body.clientTop || 0;
  const clientLeft = docEl.clientLeft || body.clientLeft || 0;
  const top = box.top + scrollTop - clientTop;
  const left = box.left + scrollLeft - clientLeft;
  return { top, left };
};
